// TradeDetailTooltip.tsx
// Tooltip content showing raw trade data + matched signals.
// Rendered inside a MUI Tooltip wrapping the trade row.

import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import type { MarketTrade } from '../api/useMarketTrades';
import type { SignalItem } from '../api/types';
import { formatVolume } from '../utils/format';
import { SignalTooltip } from './SignalTooltip';
import { TRADER_GROUP_COLORS, SIGNAL_TYPE_LABELS } from './signalConstants';

// ─── Constants ───────────────────────────────────────────────────────────────

const POLYGONSCAN_BASE = 'https://polygonscan.com';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function shortId(id: string | undefined): string {
    if (!id) return '—';
    if (id.length <= 12) return id;
    return `${id.slice(0, 6)}…${id.slice(-4)}`;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function TradeDetailContent({
    trade,
    matchedSignals,
}: {
    trade: MarketTrade;
    matchedSignals?: SignalItem[];
}) {
    const theme = useTheme();

    const labelSx = {
        fontSize: '9px',
        fontFamily: 'monospace',
        color: 'text.disabled',
        minWidth: 48,
        flexShrink: 0,
    } as const;
    const valueSx = {
        fontSize: '10px',
        fontFamily: 'monospace',
        color: 'text.secondary',
    } as const;
    const linkStyle: React.CSSProperties = {
        fontSize: '10px',
        fontFamily: 'monospace',
        color: '#64b5f6',
        textDecoration: 'none',
        cursor: 'pointer',
    };
    const rowSx = {
        display: 'flex',
        alignItems: 'baseline',
        gap: 0.75,
        mb: '3px',
    } as const;

    return (
        <Box sx={{ minWidth: 220 }}>
            {/* TX hash — use on-chain hash when available, fall back to transaction_id */}
            <Box sx={rowSx}>
                <Typography sx={labelSx}>tx</Typography>
                <a
                    href={`${POLYGONSCAN_BASE}/tx/${trade.txHash ?? trade.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={linkStyle}
                >
                    {shortId(trade.txHash ?? trade.id)}
                </a>
            </Box>

            {/* Maker/taker (only if known) */}
            {trade.isMaker !== null && (
                <Box sx={rowSx}>
                    <Typography sx={labelSx}>type</Typography>
                    <Typography
                        sx={{
                            ...valueSx,
                            color: trade.isMaker ? '#7c8aff' : '#ff9800',
                            fontWeight: 600,
                            fontSize: '9px',
                        }}
                    >
                        {trade.isMaker ? 'maker' : 'taker'}
                    </Typography>
                </Box>
            )}

            {/* Amount: base tokens (USD) */}
            <Box sx={rowSx}>
                <Typography sx={labelSx}>amount</Typography>
                <Typography sx={valueSx}>
                    {trade.amount.toLocaleString('en-US', {
                        maximumFractionDigits: 0,
                    })}
                    <Typography
                        component="span"
                        sx={{ ...valueSx, color: 'text.disabled', ml: 0.5 }}
                    >
                        ({formatVolume(trade.quoteAmount)})
                    </Typography>
                </Typography>
            </Box>

            {/* Price */}
            <Box sx={rowSx}>
                <Typography sx={labelSx}>price</Typography>
                <Typography sx={valueSx}>${trade.price.toFixed(2)}</Typography>
            </Box>

            {/* Divider */}
            <Box
                sx={{
                    borderTop: 1,
                    borderColor: alpha(theme.palette.divider, 0.15),
                    my: 0.75,
                }}
            />

            {/* Market ID */}
            <Box sx={rowSx}>
                <Typography sx={labelSx}>market</Typography>
                <a
                    href={`https://clob.polymarket.com/markets/${trade.marketId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={linkStyle}
                >
                    {shortId(trade.marketId)}
                </a>
            </Box>

            {/* Matched signals */}
            {matchedSignals && matchedSignals.length > 0 && (
                <>
                    <Box
                        sx={{
                            borderTop: 1,
                            borderColor: alpha(theme.palette.divider, 0.15),
                            my: 0.75,
                        }}
                    />
                    {matchedSignals.map(sig => {
                        const groupColor =
                            TRADER_GROUP_COLORS[sig.trader_group] ?? '#6b7280';
                        const factDelta = (sig.fact?.delta_usd ?? null) as
                            | number
                            | null;

                        const card = (
                            <Box
                                component={sig.address ? 'a' : 'div'}
                                {...(sig.address
                                    ? {
                                          href: `/markets/trader/${sig.address}?market=${sig.market_id}`,
                                          target: '_blank',
                                          rel: 'noopener',
                                      }
                                    : {})}
                                onClick={(e: React.MouseEvent) =>
                                    e.stopPropagation()
                                }
                                sx={{
                                    display: 'block',
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    bgcolor: alpha(
                                        theme.palette.text.primary,
                                        0.03
                                    ),
                                    borderRadius: 1,
                                    p: 0.75,
                                    mb: 0.5,
                                    cursor: sig.address ? 'pointer' : 'default',
                                    transition: 'background 0.15s',
                                    '&:hover': sig.address
                                        ? {
                                              bgcolor: alpha(
                                                  theme.palette.text.primary,
                                                  0.06
                                              ),
                                          }
                                        : {},
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            color:
                                                sig.direction === 'bullish'
                                                    ? 'success.main'
                                                    : sig.direction ===
                                                        'bearish'
                                                      ? 'error.main'
                                                      : 'text.disabled',
                                        }}
                                    >
                                        {sig.direction === 'bullish'
                                            ? '▲'
                                            : sig.direction === 'bearish'
                                              ? '▼'
                                              : '◆'}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '10px',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {SIGNAL_TYPE_LABELS[sig.signal_type] ??
                                            sig.signal_type.replace(/_/g, ' ')}
                                    </Typography>
                                    <Box
                                        sx={{
                                            ml: 'auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '3px',
                                            px: 0.5,
                                            py: '1px',
                                            borderRadius: '3px',
                                            bgcolor: alpha(groupColor, 0.15),
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 4,
                                                height: 4,
                                                borderRadius: '50%',
                                                bgcolor: groupColor,
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: '9px',
                                                fontWeight: 600,
                                                color: groupColor,
                                            }}
                                        >
                                            {sig.trader_group}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1,
                                        pl: 2,
                                        mt: '2px',
                                        fontSize: '9px',
                                        fontFamily: 'monospace',
                                        color: 'text.disabled',
                                    }}
                                >
                                    {factDelta != null && (
                                        <Typography
                                            component="span"
                                            sx={{
                                                fontSize: '9px',
                                                fontFamily: 'monospace',
                                                color:
                                                    factDelta >= 0
                                                        ? 'success.main'
                                                        : 'error.main',
                                            }}
                                        >
                                            {factDelta >= 0 ? '+' : '-'}
                                            {formatVolume(Math.abs(factDelta))}
                                        </Typography>
                                    )}
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: '9px',
                                            fontFamily: 'monospace',
                                            color: 'text.disabled',
                                        }}
                                    >
                                        score {sig.score.toFixed(2)}
                                    </Typography>
                                </Box>
                            </Box>
                        );

                        return (
                            <SignalTooltip key={sig.id} signal={sig}>
                                {card}
                            </SignalTooltip>
                        );
                    })}
                </>
            )}
        </Box>
    );
}
